<template>
  <div class="filter-chips" role="radiogroup" :aria-label="props.ariaLabel">
    <button
      v-for="chip in visibleChips"
      :key="chip.value"
      type="button"
      role="radio"
      class="filter-chips__chip"
      :class="{ 'filter-chips__chip--selected': chip.value === props.modelValue, 'filter-chips__chip--with-icon': Boolean(chip.categoryOption) }"
      :aria-checked="chip.value === props.modelValue"
      @click="emit('update:modelValue', chip.value === props.modelValue ? '' : chip.value)"
    >
      <LocationCategoryIcon v-if="chip.categoryOption" :option="chip.categoryOption" :size="24" />
      <span>{{ chip.label }}</span>
      <span v-if="chip.count != null" class="filter-chips__count">{{ chip.count }}</span>
    </button>
    <SrpDropdown v-if="overflowChips.length" placement="bottom-start" :offset="4" :width="240">
      <template #clickableElement="{ isDropdownVisible }">
        <button
          ref="overflowTrigger"
          type="button"
          class="filter-chips__chip"
          :class="{ 'filter-chips__chip--selected': selectedOverflowChip }"
          :aria-label="`${props.overflowLabel} (${overflowChips.length})${selectedOverflowChip ? `: ${selectedOverflowChip.label}` : ''}`"
          :aria-expanded="isDropdownVisible"
        >
          <template v-if="selectedOverflowChip">
            <span>{{ selectedOverflowChip.label }}</span>
            <span v-if="selectedOverflowChip.count != null" class="filter-chips__count">{{ selectedOverflowChip.count }}</span>
          </template>
          <span>{{ overflowChips.length }} more</span>
        </button>
      </template>
      <template #dropdown="{ close, isDropdownVisible }">
        <div class="filter-chips__overflow" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" :aria-label="props.overflowLabel" role="group" @keydown.esc.stop="closeOverflow(close)">
          <button
            v-for="chip in overflowChips"
            :key="chip.value"
            type="button"
            class="filter-chips__option"
            :class="{ 'filter-chips__option--selected': chip.value === props.modelValue }"
            :aria-pressed="chip.value === props.modelValue"
            @click="selectOverflow(chip.value, close)"
          >
            <span>{{ chip.label }}</span>
            <span v-if="chip.count != null" class="filter-chips__count">{{ chip.count }}</span>
          </button>
        </div>
      </template>
    </SrpDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";
import type { LocationCategoryOption } from "@logic/LocationCategories";

// Clicking the selected chip again clears it (back to the "All" chip).
export interface FilterChip {
  /** "" is the "All" chip. */
  value: string;
  label: string;
  count?: number;
  categoryOption?: LocationCategoryOption;
}

const props = defineProps<{
  modelValue: string;
  chips: FilterChip[];
  ariaLabel: string;
  /** Includes the "All" chip. Omit to show every chip. */
  maxVisible?: number;
  overflowLabel?: string;
}>();
const emit = defineEmits<{ (e: "update:modelValue", value: string): void }>();

const visibleChips = computed(() => props.chips.slice(0, props.maxVisible ?? props.chips.length));
const overflowChips = computed(() => props.chips.slice(props.maxVisible ?? props.chips.length));
const selectedOverflowChip = computed(() => overflowChips.value.find(chip => chip.value === props.modelValue));
const overflowTrigger = ref<HTMLButtonElement | null>(null);

function closeOverflow(close: () => void) {
  close();
  overflowTrigger.value?.focus();
}

function selectOverflow(value: string, close: () => void) {
  emit("update:modelValue", value === props.modelValue ? "" : value);
  closeOverflow(close);
}
</script>

<style scoped lang="scss">
// Filter chips ===============================================================
.filter-chips {
  gap: 8px;
  display: flex;
  flex-wrap: wrap;

  &__chip {
    height: 34px;
    padding: 0 13px;
    gap: 7px;
    display: inline-flex;
    align-items: center;
    border: 1px solid #d8dce2;
    border-radius: 100px;
    background: #fff;
    color: #28343b;
    font: 400 13px/1 sans-serif;
    white-space: nowrap;
    cursor: pointer;

    &--with-icon {
      padding-left: 5px;
    }

    &:hover {
      border-color: #087f82;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
    }

    &--selected {
      border-color: #058587;
      background: #e9f5f4;
      color: #05696b;
    }
  }

  &__count {
    color: #667085;
  }

  &__overflow {
    max-height: 300px;
    padding: 5px;
    overflow-y: auto;
  }

  &__option {
    width: 100%;
    padding: 10px;
    gap: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 0;
    border-radius: 4px;
    background: #fff;
    color: #28343b;
    font: 400 13px/18px sans-serif;
    text-align: left;
    overflow-wrap: anywhere;
    cursor: pointer;

    &:hover,
    &--selected {
      background: #e9f5f4;
      color: #05696b;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: -2px;
    }
  }
}
</style>
